import type { BodyPoints, CharacterBody, Landmarks } from '@/schema/character'

export type LandmarkScope = 'base' | 'clip' | 'frame'
export const CANONICAL_POINTS = ['feet', 'head_top', 'hand', 'hand2', 'mouth'] as const
type Canonical = (typeof CANONICAL_POINTS)[number]
export const isCanonical = (name: string): name is Canonical => (CANONICAL_POINTS as readonly string[]).includes(name)
export const clampPercent = (n: number) => Math.round(Math.max(0, Math.min(100, n)) * 100) / 100

function pointMap(points?: Partial<BodyPoints>): Landmarks {
  const result: Landmarks = {}
  for (const name of CANONICAL_POINTS) if (points?.[name]) result[name] = points[name]!
  return result
}

export function resolveLandmarks(body: CharacterBody, clipId: string, frame: number): Landmarks {
  const clip = body.clips.find((c) => c.id === clipId)
  return { ...body.landmarks, ...pointMap(body.points), ...clip?.landmarks, ...pointMap(clip?.points), ...clip?.frame_landmarks?.[String(frame)] }
}

export function setLandmark(body: CharacterBody, clipId: string, frame: number, scope: LandmarkScope, name: string, value: [number, number]): CharacterBody {
  const next = structuredClone(body)
  const point: [number, number] = [clampPercent(value[0]), clampPercent(value[1])]
  if (scope === 'base') {
    if (isCanonical(name)) next.points[name] = point
    else next.landmarks = { ...next.landmarks, [name]: point }
  } else {
    const clip = next.clips.find((c) => c.id === clipId)
    if (!clip) return body
    if (scope === 'frame') clip.frame_landmarks = { ...clip.frame_landmarks, [String(frame)]: { ...clip.frame_landmarks?.[String(frame)], [name]: point } }
    else if (isCanonical(name)) clip.points = { ...clip.points, [name]: point }
    else clip.landmarks = { ...clip.landmarks, [name]: point }
  }
  return next
}

export function removeLandmark(body: CharacterBody, clipId: string, frame: number, scope: LandmarkScope, name: string): CharacterBody {
  const next = structuredClone(body)
  if (scope === 'base') {
    if (isCanonical(name)) {
      if (name !== 'feet' && name !== 'head_top') delete next.points[name]
    } else if (next.landmarks) delete next.landmarks[name]
  } else {
    const clip = next.clips.find((c) => c.id === clipId)
    if (!clip) return body
    if (scope === 'frame') {
      const framePoints = clip.frame_landmarks?.[String(frame)]
      if (framePoints) delete framePoints[name]
    } else if (isCanonical(name)) { if (clip.points) delete clip.points[name] }
    else if (clip.landmarks) delete clip.landmarks[name]
  }
  return next
}
